import "./error.tokens";
import axios from "axios";
import {
  Configuration,
  ClientPublicApi,
  ClientPrivateApi,
  DeepLinkApi,
  QRCodeApi,
  HotlinkApi,
} from "@citychallenge/api-public";
import { AuthService } from "../auth";
import { StorageService } from "../storage";
import { ServiceInterface } from "../service.interface";
import { TranslationStore } from "../../i18n/translation-store";
import { observable, action, computed } from "mobx";
import { createContext } from "react";
import { Environment } from "../../environment";
import { DeviceService } from "../device/device-service";
import { isRejectedSessionResponse } from "./session-expiry";

if (__DEV__) {
  // Add a response interceptor
  axios.interceptors.response.use(undefined, function (error) {
    console.groupCollapsed("[dev] Request error");
    console.log(error.request, error.response);
    console.groupEnd();
    return Promise.reject(error);
  });
}

axios.interceptors.request.use((config) => {
  return config;
});

axios.interceptors.response.use(undefined, (err) => {
  if (err.response?.config?.clientRequest) {
    if (err.response.data?.message) {
      err.message = err.response.data.message;
    }
  }
  return Promise.reject(err);
});

class ApiConfig implements Configuration {
  @observable
  public basePath: string;

  @action
  public setBasePath(path: string) {
    this.basePath = path;
  }

  public get accessToken() {
    return this.authService.currentAccessToken?.id;
  }

  constructor(private authService: AuthService, defaultPath: string) {
    this.basePath = defaultPath;
  }

  public get baseOptions() {
    const lang = TranslationStore.activeLanguage;

    let deviceOs = "Web";
    if (Environment.OS === "android") deviceOs = "Android";
    if (Environment.OS === "ios") deviceOs = "iOS";

    return {
      timeout: 10000,
      clientRequest: true,
      headers: {
        ["X-App-Bundle-ID"]: Environment.identifier,
        ["X-App-Version"]: Environment.version,
        ["X-App-Build-Number"]: Environment.buildNumber,
        ["X-App-Language-Long"]: lang,
        ["X-App-Language-Short"]: lang,
        ["X-App-Timezone"]:
          typeof Intl !== "undefined" && Intl.DateTimeFormat
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : undefined,
        ["x-device-os"]: deviceOs,
        ["x-device-os-version"]: Environment.systemVersion,
        ["x-device-model"]: Environment.deviceId,
      },
    };
  }
}

export const ApiServiceContext = createContext<ApiService>(null as any);

export class ApiService implements ServiceInterface {
  public context = ApiServiceContext;

  private static KEY_STAGING_SWITCH = "KEY_STAGING_SWITCH";

  private config: ApiConfig;
  public clientPublic!: ClientPublicApi;
  public clientPrivate!: ClientPrivateApi;
  public deepLink!: DeepLinkApi;
  public qrCode!: QRCodeApi;
  public hotlink!: HotlinkApi;

  private API_PATH = __DEV__
    ? this.deviceService.getDevUrl?.() ||
      process.env.REACT_APP_API_URL ||
      "http://localhost:3001"
    : this.deviceService.getApiPath?.() || `https://api.citychallenge.app`;

  private API_STAGING_PATH = "https://api.staging.citychallenge.app";

  @computed
  public get isStaging() {
    return this.config.basePath === this.API_STAGING_PATH;
  }

  @computed
  public get basePath() {
    return this.config.basePath;
  }

  @computed
  public get clientUploadUri() {
    return `${this.basePath}/client/upload`;
  }

  /**
   * Headers the generated client sends, for requests made outside it.
   */
  public get requestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    for (const [key, value] of Object.entries(
      this.config.baseOptions.headers
    )) {
      if (value !== undefined) headers[key] = value;
    }
    const token = this.config.accessToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }

  constructor(
    public authService: AuthService,
    private storageService: StorageService,
    private deviceService: DeviceService
  ) {
    this.config = new ApiConfig(authService, this.API_PATH);
    this.initializeApis();

    // The one place the app learns its stored session has died. Registered
    // here rather than at module level because it needs this instance's
    // AuthService; not in initializeApis, which runs again on a base-path
    // switch. Concurrent 401s are safe: the first clears the token, the rest
    // no longer match it. Ejected on unmount — axios is shared, and a service
    // provider can be torn down and rebuilt, so a handler per instance would
    // otherwise pile up, each closing over a dead AuthService.
    this.sessionInterceptor = axios.interceptors.response.use(
      undefined,
      (err) => {
        if (
          isRejectedSessionResponse(
            err,
            this.authService.currentAccessToken?.id
          )
        ) {
          this.authService.expireSession();
        }
        return Promise.reject(err);
      }
    );
  }

  private sessionInterceptor?: number;

  public serviceWillUnmount() {
    if (this.sessionInterceptor !== undefined) {
      axios.interceptors.response.eject(this.sessionInterceptor);
      this.sessionInterceptor = undefined;
    }
  }

  private initializeApis() {
    this.clientPublic = new ClientPublicApi(this.config);
    this.clientPrivate = new ClientPrivateApi(this.config);
    this.deepLink = new DeepLinkApi(this.config);
    this.qrCode = new QRCodeApi(this.config);
    this.hotlink = new HotlinkApi(this.config);
  }
  public async serviceWillMount() {
    const switchedToStaging = await this.storageService.getItem(
      ApiService.KEY_STAGING_SWITCH
    );

    if (switchedToStaging) {
      this.config.setBasePath(this.API_STAGING_PATH);
      this.initializeApis();
    }
  }

  /**
   * Switch API base path to staging and clear access token
   */
  public switchToStaging = async () => {
    await this.storageService.setItem(ApiService.KEY_STAGING_SWITCH, "true");
    this.config.setBasePath(this.API_STAGING_PATH);
    this.authService.setAccessToken(undefined);
    this.initializeApis();
  };

  /**
   * Switch API base path to production and clear access token
   */
  public unSwitchFromStaging = async () => {
    await this.storageService.removeItem(ApiService.KEY_STAGING_SWITCH);
    this.config.setBasePath(this.API_PATH);
    this.authService.setAccessToken(undefined);
    this.initializeApis();
  };
}
